import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { getReportingTimeZone } from './report-timezone';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(value: number | string | null | undefined): string {
  if (value == null) return 'N/A';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

export function formatNumber(value: number | string | null | undefined): string {
  if (value == null) return '0';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0';
  return new Intl.NumberFormat('en-US').format(num);
}

export function maskAccountNumber(accountNumber: string | null | undefined): string {
  if (!accountNumber) return 'N/A';
  if (accountNumber.length <= 4) return accountNumber;
  return '****' + accountNumber.slice(-4);
}

/**
 * Format an ISO timestamp as date + time, e.g. "5/11/2026, 9:30 AM". Use this
 * for true timestamps (createdAt, processedAt, submittedAt). For date-only
 * fields (dueDate, effectiveDate, signupDate) use toLocaleDateString directly
 * so the time portion doesn't show up where it'd be misleading.
 *
 * The time is always rendered in US Pacific (PST/PDT) — the business operates on
 * the Pacific calendar day and every report filter is Pacific-anchored — so a
 * timestamp reads identically for every user regardless of their device
 * timezone (previously it used the device zone, so the same record showed a
 * different time to a user in EST vs PST). Only the display zone is fixed; the
 * user's locale still controls the number/format style.
 */
export function formatDateTime(value: string | number | Date | null | undefined): string {
  if (value == null || value === '') return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    timeZone: getReportingTimeZone(),
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Short date for due dates and next-run dates — no time component. */
export function formatShortDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.slice(0, 10);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
